import React, { FC, ReactElement, useEffect } from 'react';
import { connect } from 'react-redux';
import StateInterface from '../../../redux/types';
import { getOverlayById } from '../../../redux/overlays/selectors';
import { hideOverlay as hideOverlayAction } from '../../../redux/overlays/actions';
import usePrevious from '../../../hooks/usePrevious';
import ScrollableArea from '../ScrollableArea';
import ErrorBoundary from '../../Generic/ErrorBoundaries';
import GenericErrorRetry from '../../Generic/ErrorPages/GenericErrorRetry';
import StyledWrapper from './StyledWrapper';
import StyledContent from './StyledContent';
import Header from './Header';
import StyledBackdrop from './StyledBackdrop';

export interface BaseOverlayProps {
    children: React.ReactElement;
    identifier: string;
    isActive?: boolean;
    hasUndismissibleBackdrop?: boolean;
    dismissFunction?: (event: React.MouseEvent<HTMLDivElement>) => void;
    isFullWidth?: boolean;
    hasHeader?: boolean;
    showOverlay?: Function;
    hideOverlay?: Function;
    callback?: Function;
    className?: string;
}

const BaseOverlay: FC<BaseOverlayProps> = ({
    children = null,
    identifier,
    isActive = false,
    hasHeader = true,
    hasUndismissibleBackdrop = false,
    isFullWidth = true,
    dismissFunction,
    hideOverlay = () => {},
    callback = () => {},
    className = '',
}): ReactElement => {
    const handelDismissClick = (): void => {
        hideOverlay(identifier);
    };

    const didOverlayClose = usePrevious(isActive) && !isActive;

    useEffect(() => {
        callback();
    }, [callback, didOverlayClose]);

    const backDropDismissFunction = dismissFunction || handelDismissClick;
    const id = identifier.toLocaleLowerCase();
    return (
        <>
            <ErrorBoundary boundaryName={`Popup-${id}`} fallbackComponent={<GenericErrorRetry />}>
                {isActive ? (
                    <>
                        <StyledWrapper data-testid={id}>
                            <StyledContent isFullWidth={isFullWidth} className={className}>
                                <ScrollableArea>
                                    {hasHeader && (
                                        <Header handelDismissClick={handelDismissClick} identifier={identifier} />
                                    )}
                                    {children}
                                </ScrollableArea>
                            </StyledContent>
                            <StyledBackdrop
                                onClick={!hasUndismissibleBackdrop ? backDropDismissFunction : () => {}}
                                data-identifier={identifier}
                                data-testid="popup-backdrop"
                            />
                        </StyledWrapper>
                    </>
                ) : null}
            </ErrorBoundary>
        </>
    );
};

const mapStateToProps = (state: StateInterface, ownProps: BaseOverlayProps): {} => {
    const overlay = getOverlayById(state.overlays, ownProps.identifier);
    return { isActive: overlay.isActive };
};

const mapDispatchToProps = (dispatch: Function): {} => ({
    hideOverlay: (id: string) => dispatch(hideOverlayAction(id)),
});

export default connect(mapStateToProps, mapDispatchToProps)(BaseOverlay);
